/** マイページ開設 */
import React, { useState, useEffect, useReducer } from 'react';
import { render } from 'react-dom';
import axios from 'axios';
import { handleBrowserBack } from '../../BrowserBack';
import StepNavigation from '../../molecules/StepNavigation';
import Heading from '../../molecules/Heading';
import Block from '../../molecules/Block';
import Annotation from '../../atoms/Annotation';
import CenterButton from '../../atoms/CenterButton';
import TransitionButton from '../../molecules/TransitionButton';
import TwoText from '../../molecules/TwoText';
import { usage_pattern_type } from '../../Const';
import LoadingModal from '../../molecules/LoadingModal';
import { KWANKGM00060A02 } from '../../const/api_modules';
import NameInput2 from '../../molecules/NameInput2';
import MyPageOpenSelectJa from '../../molecules/MyPageOpenSelectJa';

interface JaInfo {
  group_code: string;
  name: string;
}

const initialErrorState = {
  email: '',
  email_confirmation: '',
  password: '',
  password_confirmation: '',
  name: '',
  ja_code: '',
  ja_name: '',
};

const MypageEntry: React.FC = () => {
  // URLパラメータ取得
  const url_params = new URLSearchParams(document.location.search.substring(1));
  const [state, setState] = useState({
    email: '', // メールアドレス
    email_confirmation: '', // メールアドレス(確認用)
    password: '', // パスワード
    password_confirmation: '', // パスワード(確認用)
    name: url_params.get('name') ?? '', // お名前
    ja_code: url_params.get('jacode') ?? '', // JA金融機関コード
    ja_name: '', // JA名
    usage_pattern: url_params.get('usage_pattern'), // 利用形態
  });
  // お名前disabled制御
  const [name_disabled, setNameDisabled] = useState(false);
  // メールアドレスdisabled制御
  const [email_disabled, updateEmailDisabled] = useReducer(() => true, false);
  // お取引先JA
  const [ja_input_disabled, updateJaInputDisabled] = useReducer(() => false, true);
  // ポップアップ
  const [modal_show, setModalShow] = useState(false);
  // エラーメッセージ
  const [error_message, setErrorMessage] = useState(initialErrorState);
  // ローディング画面表示制御
  const [is_loading, setIsLoading] = useState(false);

  const node = document.getElementById('mypage-entry');
  const referer = node?.dataset.referer as string;
  const r_cd = node?.dataset.r_cd as string;
  const r_id = Number(node?.dataset.r_id as string);
  const is_ja_exists = node?.dataset.is_ja_exists as string;

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowserBack();
    inisializeState();

    // リファラを取得できる場合、戻り先URLを保存する
    if (referer) {
      sessionStorage.setItem('mypage_entry_referer', referer);
    }
  }, []);

  // 各項目の入力時
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const key = event.target.id;
    const value = event.target.value;
    setState(prev => ({ ...prev, [key]: value }));
  };

  // お名前入力時の処理
  const handleChangeName = (value: string) => {
    setState(prev => ({ ...prev, name: value }));
  };

  // ポップアップの開閉
  const togglePopup = () => setModalShow(modal_show => !modal_show);

  // マイページ開設ボタン押下時
  const handleClick = async () => {
    try {
      setIsLoading(true);

      // エラー表示の初期化
      setErrorMessage(initialErrorState);

      // POSTする関数の配列
      const api = [
        // マイページ開設処理
        axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/entry/entry_proc`, state),
      ];

      // 案件コード,案件IDがあるか判定
      if (r_cd && r_id) {
        // 借入申込案件作成処理
        api.push(
          axios.post(`${location.origin}${process.env.MIX_MOCK_URL}/api/common`, {
            module_name: KWANKGM00060A02,
            reception_cd: r_cd,
            reception_id: r_id,
          })
        );
      }

      const [mypage_open, create_reception] = await axios.all(api);

      if (
        (mypage_open.data.result && typeof create_reception === 'undefined') ||
        (mypage_open.data.result && create_reception.data.result)
      ) {
        // 画面遷移
        location.href = mypage_open.data.url;
      } else {
        // エラーメッセージの格納
        setErrorMessage(mypage_open.data.message);
        if (name_disabled && mypage_open.data.message?.name?.length > 0) {
          alert(mypage_open.data.message?.name[0]);
        }
        setIsLoading(false);
      }
    } catch (error) {
      console.error(error);
      alert('システムエラーが発生しました');
      setIsLoading(false);
    }
  };

  // 戻るボタン押下時
  const backTransition = () => {
    if (referer) {
      // リファラが渡ってくる場合はリファラに示されたURLに遷移する
      location.replace(referer);
    } else {
      // リファラが渡ってきてない場合は事前に保存したリファラのURLに遷移する
      location.replace(sessionStorage.getItem('mypage_entry_referer') ?? '');
    }
  };

  // JA検索
  const searchJa = async (): Promise<void> => {
    try {
      //  エラー初期化
      setErrorMessage({ ...error_message, ja_code: '', ja_name: '' });

      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/entry/ja_search`,
        {
          ja_code: state.ja_code,
        }
      );
      setState(prev => ({ ...prev, ja_name: response.data.detail.ja_name }));
    } catch (error) {
      console.error(error);
      alert(error.response.data.message);
    }
  };

  /**
   * 初期値があれば、入力欄に初期値を表示し入力不可にする
   * 初期値が無ければ、入力欄を入力可能にする
   */
  const inisializeState = () => {
    searchJa();

    const param_email = url_params.get('mail');

    if (param_email) {
      setState(prev => ({
        ...prev,
        email: decodeURIComponent(param_email),
        email_confirmation: decodeURIComponent(param_email),
      }));
      updateEmailDisabled();
    }

    if (state.name) {
      setNameDisabled(true);
    }

    if (!is_ja_exists) {
      // 画面初期表示時にURLパラメータのJAが存在していなかった場合
      updateJaInputDisabled();
    }
  };

  // ヘッダ部分の表示切替
  const returnHeading = () => {
    if (state.usage_pattern === usage_pattern_type.normal) {
      return <Heading label={'マイページ開設'} size={'l'} />;
    } else {
      return (
        <>
          <Heading label={'借入申込事前準備'} size={'l'} />
          <StepNavigation
            current={2}
            step1={'事前審査結果確認'}
            step2={'マイページ開設'}
            step3={'完了'}
            disabled={false}
          />
        </>
      );
    }
  };

  // ポップアップ内のJA一覧を押下時
  const popupClick = (ja_info: JaInfo) => {
    //  エラー初期化
    setErrorMessage({ ...error_message, ja_code: '', ja_name: '' });
    setState(prev => ({ ...prev, ja_code: ja_info.group_code, ja_name: ja_info.name }));
    setModalShow(false);
  };

  return (
    <>
      {returnHeading()}
      <Block
        label={'マイページ開設'}
        content={
          <>
            <Annotation
              text={
                'メールアドレス、パスワード、お名前、JA金融機関コードを入力してマイページを開設して下さい。'
              }
            />
            <div className="mt-3">
              <TwoText
                id1={'email'}
                id2={'email_confirmation'}
                label={'ログインIDの設定'}
                type={'text'}
                heading1={'メールアドレス'}
                heading2={'メールアドレス(確認用)'}
                value1={state.email}
                value2={state.email_confirmation}
                placeholder={'example@example.com'}
                handleChange1={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
                handleChange2={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
                help_msg={
                  email_disabled
                    ? ''
                    : '※今後こちらのメールアドレス宛に重要なお知らせをお届けします。入力内容に間違いのないことを必ず確認して下さい'
                }
                message1={error_message.email}
                message2={error_message.email_confirmation}
                disabled={email_disabled}
                maxlength={50}
              />
              <TwoText
                id1={'password'}
                id2={'password_confirmation'}
                label={'パスワードの設定'}
                type={'password'}
                heading1={'パスワード'}
                heading2={'パスワード(確認用)'}
                value1={state.password}
                value2={state.password_confirmation}
                placeholder={'XXXXXX'}
                handleChange1={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
                handleChange2={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
                help_msg={
                  '※半角英大文字、英小文字、数字、記号のうち3種以上を指定し8桁以上で設定して下さい'
                }
                message1={error_message.password}
                message2={error_message.password_confirmation}
                disabled={false}
                maxlength={256}
              />
              <NameInput2
                key={''}
                id={''}
                label={'お名前の設定'}
                heading={'お名前'}
                name={'name'}
                placeholder={'田中　太郎'}
                disabled={name_disabled}
                value={state.name}
                maxlength={25}
                message={error_message.name}
                help_msg={name_disabled ? '' : '※左項目に姓、右項目に名を入力して下さい'}
                handleChangeFirstName={value => handleChangeName(value)}
                handleChangeLastName={value => handleChangeName(value)}
                handleBlurFirstName={() => {}}
                handleBlurLastName={() => {}}
              />
              <TwoText
                id1={'ja_code'}
                id2={'ja_name'}
                label={'お取引先JAの設定'}
                type={'button'}
                heading1={'JA金融機関コード'}
                heading2={'JA名'}
                value1={state.ja_code}
                value2={state.ja_name || ''}
                placeholder={'1234'}
                handleChange1={(event: React.ChangeEvent<HTMLInputElement>) => handleChange(event)}
                handleBlur={searchJa}
                help_msg={
                  ja_input_disabled ? '' : '※お取引先JAから指定されたコードを入力して下さい'
                }
                message1={error_message.ja_code}
                message2={error_message.ja_name}
                disabled={ja_input_disabled}
                btn_disabled={ja_input_disabled}
                handleClick={togglePopup}
              />
            </div>
            <div className="content-box">
              <CenterButton
                btn_class={'arrow-right'}
                label={'マイページを開設する'}
                handleClick={handleClick}
              />
              <TransitionButton label={'戻る'} handleClick={backTransition} />
            </div>
          </>
        }
      />
      <MyPageOpenSelectJa
        toggleShow={modal_show}
        toggleClose={togglePopup}
        popupClick={popupClick}
      />
      <LoadingModal show={is_loading} />
    </>
  );
};

if (document.getElementById('mypage-entry')) {
  render(<MypageEntry />, document.getElementById('mypage-entry'));
}
